"use client"

import React, { useState, useEffect, useCallback } from "react"
import { fabric } from "fabric"
import { DropletIcon, Shield } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card"
import __ from "../utils/translation"
import Button from "./Button"

type DTFProps = {
    canvas: fabric.Canvas | null
}

const DTF = ({ canvas }: DTFProps) => {
    const [brilhoEnabled, setBrilhoEnabled] = useState(true)
    const [brilhoValue, setBrilhoValue] = useState(100)

    const [metalEnabled, setMetalEnabled] = useState(false)
    const [metalValue, setMetalValue] = useState(50)

    const [relevoEnabled, setRelevoEnabled] = useState(true)
    const [relevoValue, setRelevoValue] = useState(100)

    const applyEffect = useCallback((type: string, value: number, enabled: boolean) => {
        window.dispatchEvent(new CustomEvent("dtf:apply", {
            detail: { type, value, enabled }
        }))
    }, [])

    const resetAll = () => {
        setBrilhoEnabled(true)
        setBrilhoValue(100)
        setMetalEnabled(false)
        setMetalValue(50)
        setRelevoEnabled(true)
        setRelevoValue(100)
        window.dispatchEvent(new CustomEvent("dtf:reset"))
    }

    useEffect(() => {
        const syncFromRelevo = () => {
            const r = (window as any).relevo
            if (!r) return
            const hasBrilho = r.valRough != null
            const hasMetal = r.valMetal != null
            const hasRelevo = r.valBump != null
            setBrilhoEnabled(hasBrilho)
            setBrilhoValue(hasBrilho ? Math.round((1 - (typeof r.valRough === 'number' ? r.valRough : 0)) * 100) : 100)
            setMetalEnabled(hasMetal)
            setMetalValue(hasMetal ? Math.round((typeof r.valMetal === 'number' ? r.valMetal : 0.8) * 100) : 50)
            setRelevoEnabled(hasRelevo)
            setRelevoValue(hasRelevo ? Math.round((typeof r.valBump === 'number' ? r.valBump : 50) / 50 * 100) : 100)
        }
        syncFromRelevo()
        const onReset = () => {
            setBrilhoEnabled(true)
            setBrilhoValue(100)
            setMetalEnabled(false)
            setMetalValue(50)
            setRelevoEnabled(true)
            setRelevoValue(100)
        }
        window.addEventListener("dtf:reset", onReset)
        window.addEventListener("dtf:sync", syncFromRelevo)
        return () => {
            window.removeEventListener("dtf:reset", onReset)
            window.removeEventListener("dtf:sync", syncFromRelevo)
        }
    }, [])

    const handleToggle = (type: string, enabled: boolean, setEnabled: (v: boolean) => void, value: number) => {
        const next = !enabled
        setEnabled(next)
        applyEffect(type, value, next)
    }

    const handleSlider = (type: string, enabled: boolean, val: number, setVal: (v: number) => void) => {
        setVal(val)
        if (enabled) {
            applyEffect(type, val, true)
        }
    }

    return (
        <div className="h-full">
            <Card className="border-border/50 bg-card/85 backdrop-blur-sm !h-full rounded-none">
                <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide">
                        <Shield />
                        <span className="lang">{__("DTF")}</span>
                    </CardTitle>
                </CardHeader>

                <CardContent className="flex flex-col space-y-4">
                    <div className="p-3 bg-muted/30 rounded-md text-sm text-primary">
                        <p className="mb-1 font-medium">Como usar este menu:</p>
                        <ul className="list-disc list-inside space-y-1 text-primary text-justify">
                            <li>No canvas selecione as logos, textos e objetos que deseja aplicar os efeitos.</li>
                            <li>Depois de selecionar, ative o switch ao lado de cada opção para aplicar o efeito.</li>
                            <li>Use o slider para ajustar a intensidade do efeito.</li>
                            <li>Se deletar ou editar a posição, escala e rotaçao do objeto depois de aplicar o efeito, é preciso repetir esse processo ou resetar.</li>
                            <li>Clique em "RESETAR" para voltar aos valores padrão.</li>
                        </ul>
                    </div>

                    <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50 shadow-xl">
                        <div className="flex items-center gap-2">
                            <DropletIcon className="w-5 h-5" />
                            <span className="text-sm font-medium">Brilho</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <input
                                type="range"
                                min={0}
                                max={100}
                                value={brilhoValue}
                                onChange={(e) => handleSlider("roughness", brilhoEnabled, Number(e.target.value), setBrilhoValue)}
                                className="w-24"
                            />
                            <button
                                onClick={() => handleToggle("roughness", brilhoEnabled, setBrilhoEnabled, brilhoValue)}
                                className={`w-12 h-6 flex items-center rounded-full p-1 transition-colors duration-300
                  ${brilhoEnabled ? "bg-primary" : "bg-gray-300"}`}
                            >
                                <div
                                    className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform duration-300
                  ${brilhoEnabled ? "translate-x-6" : "translate-x-0"}`}
                                ></div>
                            </button>
                        </div>
                    </div>

                    <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50 shadow-xl">
                        <div className="flex items-center gap-2">
                            <DropletIcon className="w-5 h-5" />
                            <span className="text-sm font-medium">Metal</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <input
                                type="range"
                                min={0}
                                max={100}
                                value={metalValue}
                                onChange={(e) => handleSlider("metalness", metalEnabled, Number(e.target.value), setMetalValue)}
                                className="w-24"
                            />
                            <button
                                onClick={() => handleToggle("metalness", metalEnabled, setMetalEnabled, metalValue)}
                                className={`w-12 h-6 flex items-center rounded-full p-1 transition-colors duration-300
                  ${metalEnabled ? "bg-primary" : "bg-gray-300"}`}
                            >
                                <div
                                    className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform duration-300
                  ${metalEnabled ? "translate-x-6" : "translate-x-0"}`}
                                ></div>
                            </button>
                        </div>
                    </div>

                    <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50 shadow-xl">
                        <div className="flex items-center gap-2">
                            <DropletIcon className="w-5 h-5" />
                            <span className="text-sm font-medium">Relevo</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <input
                                type="range"
                                min={0}
                                max={100}
                                value={relevoValue}
                                onChange={(e) => handleSlider("bump", relevoEnabled, Number(e.target.value), setRelevoValue)}
                                className="w-24"
                            />
                            <button
                                onClick={() => handleToggle("bump", relevoEnabled, setRelevoEnabled, relevoValue)}
                                className={`w-12 h-6 flex items-center rounded-full p-1 transition-colors duration-300
                  ${relevoEnabled ? "bg-primary" : "bg-gray-300"}`}
                            >
                                <div
                                    className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform duration-300
                  ${relevoEnabled ? "translate-x-6" : "translate-x-0"}`}
                                ></div>
                            </button>
                        </div>
                    </div>

                    <Button className="bg-secondary h-[40px] m-3 rounded-lg" onClick={resetAll}>
                        RESETAR
                    </Button>
                </CardContent>
            </Card>
        </div>
    )
}

export default DTF
